import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { api, getToken } from "@/lib/api";
import { Check, ArrowRight, Loader2, ExternalLink, AlertCircle } from "lucide-react";

const TAX_RATE = 0.11; // PPN 11%

// Fallback plan data — sama dengan di Harga.jsx
const FALLBACK_PLANS = {
  "3bulan": { id: "3bulan", name: "3 Bulan", price: 80000, duration_days: 90, description: "Langganan 3 bulan" },
  "6bulan": { id: "6bulan", name: "6 Bulan", price: 130000, duration_days: 180, description: "Langganan 6 bulan" },
  "1tahun": { id: "1tahun", name: "1 Tahun", price: 250000, duration_days: 365, description: "Langganan 1 tahun" },
};

export default function Payment() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get("plan") || "3bulan";

  // Pakai fallback langsung biar gak loading terus
  const fallbackPlan = FALLBACK_PLANS[planId];
  const [plan, setPlan] = useState(fallbackPlan || null);
  const [loading, setLoading] = useState(!fallbackPlan);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [paymentInfo, setPaymentInfo] = useState(null);

  const token = getToken();

  useEffect(() => {
    if (!token) {
      const currentPath = window.location.pathname + window.location.search;
      nav(`/login?redirect=${encodeURIComponent(currentPath)}`, { replace: true });
      return;
    }
    // Coba fetch dari API, kalo gagal fallback udah siap
    fetchBestPlan();
  }, [planId, token, nav]);

  const fetchBestPlan = async () => {
    try {
      const { data: plans } = await api.get("/plans");
      const found = plans.find((p) => p.id === planId);
      if (found) setPlan(found);
    } catch (err) {
      // Gagal fetch, pake fallback — gak perlu error
      if (!fallbackPlan) {
        setError("Gagal memuat data paket. Periksa koneksi Anda.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePay = async () => {
    if (!plan || creating) return;
    setCreating(true);
    setError("");

    try {
      const { data } = await api.post("/payment/create", { plan_id: plan.id });

      if (data.payment_url) {
        setPaymentInfo(data);
        // Redirect user to DOKU Checkout page
        window.location.href = data.payment_url;
      } else {
        setError("Gagal mendapatkan URL pembayaran. Silakan coba lagi.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Terjadi kesalahan. Silakan coba lagi.";
      setError(msg);
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5]">
        <Loader2 className="h-6 w-6 text-[#002FA7] animate-spin" />
      </div>
    );
  }

  // Show fallback instead of white page while plan is loading
  if (!plan) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5]">
        <div className="text-center max-w-sm">
          {error ? (
            <>
              <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-3" />
              <p className="label-eyebrow text-red-600">Gagal memuat paket</p>
              <p className="text-sm text-zinc-500 mt-2 mb-4">{error}</p>
            </>
          ) : (
            <p className="label-eyebrow text-zinc-400">Memuat data paket...</p>
          )}
          <a href="/harga" className="text-sm text-[#002FA7] underline underline-offset-2 mt-2 inline-block">
            ← Kembali ke daftar paket
          </a>
        </div>
      </div>
    );
  }

  // PPN dihitung di backend — pakai response kalau ada
  const totalAmount = paymentInfo?.amount || Math.round(plan.price * (1 + TAX_RATE));
  const ppnAmount = paymentInfo?.ppn || Math.round(plan.price * TAX_RATE);

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      {/* ── Header ── */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/harga" className="flex items-center gap-2 group">
            <img src="/logo.svg" alt="DigiSign" className="h-8" />
          </Link>
          <span className="label-eyebrow hidden sm:block">Pembayaran</span>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="grid md:grid-cols-5 gap-8">
          {/* ── Left: Order Summary ── */}
          <div className="md:col-span-2">
            <div className="bg-white border border-zinc-200 shadow-sm p-6">
              <p className="label-eyebrow mb-1">Ringkasan Pesanan</p>
              <h3 className="font-display text-xl font-bold tracking-tight mb-4">{plan.name}</h3>

              <div className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-sm text-zinc-600">
                  <Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                  <span>Unlimited Layar TV</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-zinc-600">
                  <Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                  <span>Upload Gambar & Video</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-zinc-600">
                  <Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                  <span>Penjadwalan Konten</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-zinc-600">
                  <Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                  <span>Dashboard Online</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-zinc-600">
                  <Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                  <span>Support via WhatsApp</span>
                </li>
              </div>

              <div className="border-t border-zinc-100 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Harga</span>
                  <span className="font-semibold">Rp{plan.price.toLocaleString("id-ID")}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">PPN 11%</span>
                  <span className="font-semibold">Rp{ppnAmount.toLocaleString("id-ID")}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Durasi</span>
                  <span className="font-semibold">{plan.duration_days} hari</span>
                </div>
              </div>

              <div className="border-t border-zinc-100 pt-4 mt-4">
                <div className="flex justify-between">
                  <span className="text-sm font-semibold">Total Pembayaran</span>
                  <span className="font-display text-xl font-black text-[#002FA7]">
                    Rp{totalAmount.toLocaleString("id-ID")}
                  </span>
                </div>
              </div>

              {paymentInfo && (
                <div className="mt-4 bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800">
                  Pembayaran sedang diproses. Anda akan diarahkan ke halaman DOKU.
                </div>
              )}

              <Link to="/harga" className="block text-center text-xs text-zinc-400 mt-4 hover:text-[#002FA7] underline underline-offset-2">
                ← Kembali ke daftar paket
              </Link>
            </div>
          </div>

          {/* ── Right: Payment ── */}
          <div className="md:col-span-3">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight mb-1">Pilih Pembayaran</h2>
              <p className="text-sm text-zinc-500 mb-6">
                Pembayaran diproses secara aman melalui <strong>DOKU Payment Gateway</strong>.
              </p>

              {/* Error */}
              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 p-4 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-sm text-red-800">Gagal memproses pembayaran</p>
                    <p className="text-xs text-red-600 mt-0.5">{error}</p>
                  </div>
                </div>
              )}

              {/* Metode yang didukung */}
              <div className="bg-white border border-zinc-200 shadow-sm p-6 space-y-6">
                <div>
                  <p className="label-eyebrow mb-3">Metode Pembayaran yang Didukung</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      "Virtual Account BCA",
                      "Virtual Account Mandiri",
                      "Virtual Account BRI",
                      "Virtual Account BNI",
                      "Virtual Account CIMB",
                      "Virtual Account Permata",
                      "QRIS (GoPay, OVO, DANA, ShopeePay)",
                      "Alfamart / Indomaret",
                      "Kartu Kredit",
                    ].map((m) => (
                      <div key={m} className="flex items-center gap-2 text-xs text-zinc-600 bg-zinc-50 px-3 py-2">
                        <Check className="h-3 w-3 text-emerald-500 shrink-0" />
                        <span>{m}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Notice */}
                <div className="bg-[#002FA7]/5 border border-[#002FA7]/10 p-4 text-sm text-zinc-600">
                  <p className="font-semibold text-[#002FA7] mb-1">🔒 Pembayaran Aman</p>
                  <p className="text-xs">
                    Setelah klik "Bayar Sekarang", Anda akan diarahkan ke halaman pembayaran aman milik DOKU.
                    Pilih metode pembayaran favorit Anda dan selesaikan pembayaran di sana.
                  </p>
                </div>
              </div>

              {/* Pay button */}
              <button
                onClick={handlePay}
                disabled={creating}
                className={`signage-btn-primary w-full h-12 flex items-center justify-center gap-2 mt-6 text-sm ${
                  creating ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                {creating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Menyiapkan pembayaran…
                  </>
                ) : (
                  <>
                    Bayar Sekarang Rp{totalAmount.toLocaleString("id-ID")}
                    <ExternalLink className="h-4 w-4" />
                  </>
                )}
              </button>

              <p className="text-xs text-zinc-400 mt-3 text-center">
                Dengan klik bayar, Anda menyetujui syarat & ketentuan yang berlaku.
              </p>
            </div>
          </div>
        </div>
      </div>

      <footer className="bg-zinc-900 text-zinc-400 py-8 mt-10">
        <div className="max-w-4xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo.svg" alt="DigiSign" className="h-6" />
          </div>
          <p className="text-xs">
            &copy; {new Date().getFullYear()} DigiSign.Web.ID — All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
